/**
 * Hook to resolve the active Edit Chat tab from the URL hash fragment.
 * Based on the implementation from the old choco-admin-chats.
 */

import { EditChatTabNames } from '@choco-admin/types';
import { useLocation } from 'react-router-dom';

// `Object.values` on a string enum yields the tab hash strings; widening to
// `readonly string[]` lets us test membership against an arbitrary string
// without an `as` cast.
const SUPPORTED_TAB_NAMES: readonly string[] = Object.values(EditChatTabNames);

export const isEditChatTabName = (value: string): value is EditChatTabNames =>
  SUPPORTED_TAB_NAMES.includes(value);

/**
 * Resolve a raw URL hash fragment (without the leading `#`) to a supported tab.
 * Unknown, empty, or stale fragments — notably the retired `#order-guide`
 * (issue #124) — fall back to Participants so the page always lands on a real,
 * supported tab.
 */
export const resolveEditChatTab = (rawHash: string): EditChatTabNames =>
  isEditChatTabName(rawHash) ? rawHash : EditChatTabNames.PARTICIPANTS;

export interface ActiveTabName {
  /** Raw hash fragment from the URL (without the leading `#`); empty when absent. */
  rawHash: string;
  /** Always a supported tab; stale/unknown hashes resolve to Participants. */
  activeTab: EditChatTabNames;
}

export const useActiveTabName = (): ActiveTabName => {
  const { hash } = useLocation();
  const rawHash = hash.slice(1); // Remove the '#' prefix
  return { rawHash, activeTab: resolveEditChatTab(rawHash) };
};
